/**
 * The fleet: managed devices, the approval queue, retired records, and the agentless monitors.
 *
 * The port of `Views/DevicesPage.xaml` + `DevicesViewModel`. Three rules carry over:
 *
 *   * **THE THREE SERVER LISTS PARTITION THE FLEET** - managed, awaiting approval, retired - so
 *     every device is reachable from exactly one view. Pending devices used to sit in the managed
 *     list with nothing marking them unapproved; they get their own view rather than being hidden.
 *   * **Approve is not offered here.** It assigns employees, and that is the customer page's
 *     approval card; this list offers *Review & approve* (which goes there) and an inline Reject,
 *     which needs no extra input and is undone by Restore.
 *   * **A MONITOR WHOSE MACHINE RUNS THE AGENT IS NOT LISTED.** It is already above as an enrolled
 *     device and its SNMP data is that device's SNMP tab - listing it again would put one Synology
 *     in the fleet twice, which is the join `HasAgent` exists to prevent.
 *
 * Presence is a 4-second poll of one endpoint, **and** the hub's `DevicePresenceChanged` push. The
 * OpenSilver page had only the poll (its comment calls the push "an upgrade later"); having both
 * means a machine coming online shows in under a second, while the poll stays as the thing that
 * corrects the list if a push is missed.
 */
import type { Container } from 'webwin';
import * as crm from '../api/crm.js';
import * as hub from '../api/hub.js';
import { describeError } from '../api/http.js';
import {
  AgentPayloads,
  ApprovalStatus,
  ManagementChannel,
  type DeviceDto,
  type DevicePresenceDto,
  type SnmpMonitorDto
} from '../contracts.gen.js';
import { join, localDateTime } from '../format.js';
import { isWindowsCaption } from '../platform.js';
import type { Card } from '../ui/Card.js';
import { CardList } from '../ui/CardList.js';
import { chip } from '../ui/Chip.js';
import { confirm } from '../ui/dialogs.js';
import { dot } from '../ui/Dot.js';
import { EmptyState } from '../ui/EmptyState.js';
import { link } from '../ui/Link.js';
import { StatusLine } from '../ui/StatusLine.js';
import { button, buttonRow, card, comboBox, field, fillWith, FONT, GAP, h1, h2, hstack, inlineRow, muted, relayout, scrollPage, splitRow, stackOf, value, vstack } from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';
import { loadCustomerChoices, type CustomerChoices } from './sections/recurring.js';

/** The presence poll. The OpenSilver cadence, unchanged. */
const PRESENCE_POLL_MS = 4_000;

/**
 * How many presence ticks between monitor refreshes.
 *
 * Monitor state moves at poll cadence (a minute or so), not heartbeat cadence, so refreshing the
 * monitored rows every four seconds would be fifteen wasted reads per minute for a row that cannot
 * have changed.
 */
const MONITOR_EVERY_TICKS = 15;

type View = 'active' | 'pending' | 'retired';

export function devicesPage(): CrmPage {
  return {
    async mount(host: Container, ctx: PageContext) {
      let view: View = 'active';
      let devices: DeviceDto[] = [];
      let customerNames = new Map<number, string>();
      let choices: CustomerChoices = { names: [], ids: [] };
      /** Bumped per reload, so a slow read for a customer no longer selected cannot land on screen. */
      let generation = 0;
      let ready = false;
      /** Served agent version per payload flavor, so a device is measured against ITS own payload. */
      const agentVersions = new Map<string, string | null>();
      let pushing = false;
      let tick = 0;

      const status = new StatusLine();
      const pushStatus = new StatusLine();

      // ---- header ----
      const title = h1('Devices');
      const subtitle = muted('');
      const backLink = link('← Back to active devices', () => void switchView('active'));
      // Amber, like the sidebar's lockout count: a queue with something in it is the one aside on
      // this page that is a call to act rather than a place to go.
      const pendingLink = link('', () => void switchView('pending'), 'warn');
      const retiredLink = link('View archived & rejected devices', () => void switchView('retired'));
      const pushButton = button('Push update to online agents', () => void pushUpdate(), true);
      const customerFilter = comboBox<string>(['All customers'], {
        selectedIndex: 0,
        onSelectionChanged: () => void (ready && customerChanged())
      });

      const links = stackOf(vstack({ spacing: GAP.xs }), backLink, pendingLink, retiredLink);
      // One column: beside the push button, the subtitle and the links were squeezed into a third of
      // a phone's width and the links cut off.
      const header = stackOf(
        vstack({ spacing: GAP.xs }),
        title,
        subtitle,
        stackOf(buttonRow(), pushButton),
        pushStatus.label,
        links,
        stackOf(buttonRow(), field('Customer', customerFilter, { width: 220 })),
        status.label
      );

      // ---- the three device lists ----
      //
      // One CardList, re-pointed on a view switch. The OpenSilver page needed two collections
      // because a hidden ItemsControl still builds its rows - a canvas list that is not in the tree
      // costs nothing, so the rows can share one list and differ by builder.
      const deviceList = new CardList<DeviceDto>({
        key: d => d.id,
        // Presence is what changes between refreshes; naming it explicitly keeps a 4-second poll
        // from stringifying the whole fleet's hardware inventory sixteen times a minute.
        version: d => [d.presence?.isOnline, d.presence?.loggedOnUser, d.status, d.archived, d.agentVersion, d.mobile?.lastStatusReportUtc, d.mobile?.lostModeSinceUtc, d.mobile?.policyCompliant],
        build: d => (view === 'pending' ? buildPendingCard(d) : buildDeviceCard(d))
      });
      const devicesEmpty = new EmptyState('No devices yet.');

      // ---- monitored (agentless) devices ----
      const monitorList = new CardList<{ monitor: SnmpMonitorDto; customerName: string }>({
        key: row => row.monitor.id,
        version: row => [row.monitor.online, row.monitor.lastPolledUtc, row.monitor.statusLabel],
        build: row => buildMonitorCard(row.monitor, row.customerName)
      });
      const monitorSection = stackOf(
        vstack({ spacing: GAP.sm, visible: false }),
        h2('Monitored devices'),
        muted(
          "Agentless devices polled over SNMP. Enroll and remove them from each customer's Network page."
        ),
        monitorList.panel
      );

      const { view: scroller, content } = scrollPage();
      content.controls.add(
        stackOf(vstack(), header, devicesEmpty.panel, deviceList.panel, monitorSection)
      );
      host.controls.add(scroller);

      // ---------------------------------------------------------------
      //  Cards
      // ---------------------------------------------------------------

      function buildDeviceCard(d: DeviceDto): Card {
        const heading = inlineRow();
        heading.controls.add(value(d.name, { font: FONT.h2 }));
        if (isRetired(d)) {
          // "Rejected" and "Archived" are both retired records but they are not the same fact, and
          // labelling a rejected device "Archived" would describe a state it was never in.
          heading.controls.add(chip(d.status === ApprovalStatus.Rejected ? 'Rejected' : 'Archived', 'warn'));
        }

        const osRow = inlineRow();
        osRow.controls.add(value(d.os?.caption || 'Unknown OS', { font: FONT.small, role: 'muted' }));
        if (agentOutOfDate(d)) osRow.controls.add(chip('Update available', 'warn'));
        // The fleet-wide service signal: automatic services that stopped with an error or never
        // started, counted by the agent's own rule and carried on the device record.
        if (d.stoppedServices > 0)
          osRow.controls.add(chip(d.stoppedServices === 1 ? '1 service stopped' : `${d.stoppedServices} services stopped`, 'danger'));
        if (d.policyIssues > 0)
          osRow.controls.add(chip(d.policyIssues === 1 ? '1 policy issue' : `${d.policyIssues} policy issues`, 'danger'));

        // A phone has no connection to be online on: when it last reported, and whose it is, are the facts.
        const phone = d.channel === ManagementChannel.Android;
        const reported = d.mobile?.lastStatusReportUtc ?? null;
        const recent = reported !== null && Date.now() - Date.parse(reported) < 24 * 3600_000;
        if (phone) {
          osRow.controls.add(chip('Android', 'info'));
          if (d.mobile?.lostModeSinceUtc) osRow.controls.add(chip('lost mode', 'danger'));
          else if (d.mobile && !d.mobile.policyCompliant) osRow.controls.add(chip('not applying its policy', 'warn'));
        }

        const left = stackOf(vstack({ spacing: GAP.xs }), heading, osRow);

        const right = stackOf(
          vstack({ spacing: 0, autoWidth: true, align: 'end' }),
          value(phone ? (reported ? `Checked in ${localDateTime(reported)}` : 'Not checked in') : d.presence?.isOnline ? 'Online' : 'Offline', { font: FONT.bold }),
          value((phone ? d.primaryEmployeeName : d.presence?.loggedOnUser) || '—', { font: FONT.small, role: 'muted' })
        );

        const row = splitRow({ spacing: GAP.md, align: 'center' });
        row.controls.add(dot(phone ? recent : d.presence?.isOnline ?? false));
        fillWith(row, left);
        row.controls.add(right);

        const shell = card({ cursor: 'pointer' });
        shell.controls.add(row);
        shell.onActivate(() => ctx.go(`/devices/${d.id}`));
        return shell;
      }

      function buildPendingCard(d: DeviceDto): Card {
        const heading = inlineRow();
        heading.controls.add(value(d.name, { font: FONT.h2 }));
        heading.controls.add(chip('Awaiting approval', 'warn'));

        // The queue spans customers, so a row has to say which one it belongs to - `DeviceDto`
        // carries only the id.
        const enrolled = join([
          `Enrolled ${localDateTime(d.enrolledUtc)}`,
          customerNames.get(d.customerId)
        ]);

        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          heading,
          muted(d.os?.caption || 'Unknown OS'),
          muted(enrolled)
        );

        const actions = buttonRow({ autoWidth: true, hAlign: 'end' });
        // Approve is not here: it assigns employees, and that happens on the customer's card.
        actions.controls.add(button('Review & approve', () => ctx.go(`/customers/${d.customerId}`), true));
        actions.controls.add(button('Reject', () => void reject(d)));

        const row = splitRow({ align: 'center' });
        fillWith(row, left);
        row.controls.add(actions);

        const shell = card();
        shell.controls.add(row);
        return shell;
      }

      function buildMonitorCard(m: SnmpMonitorDto, customerName: string): Card {
        const heading = inlineRow();
        heading.controls.add(value(m.displayName, { font: FONT.h2 }));
        heading.controls.add(chip('Monitored', 'info'));

        const left = stackOf(
          vstack({ spacing: GAP.xs }),
          heading,
          muted(join([m.ipAddress, customerName]))
        );

        const right = stackOf(
          vstack({ spacing: 0, autoWidth: true, align: 'end' }),
          value(m.statusLabel, { font: FONT.bold }),
          value(m.lastPolledUtc ? `polled ${localDateTime(m.lastPolledUtc)}` : 'never polled', {
            font: FONT.small,
            role: 'muted'
          })
        );

        const row = splitRow({ spacing: GAP.md, align: 'center' });
        row.controls.add(dot(m.online));
        fillWith(row, left);
        row.controls.add(right);

        const shell = card({ cursor: 'pointer' });
        shell.controls.add(row);
        shell.onActivate(() => ctx.go(`/monitors/${m.id}`));
        return shell;
      }

      // ---------------------------------------------------------------
      //  Agent versions
      // ---------------------------------------------------------------

      function isRetired(d: DeviceDto): boolean {
        return d.archived || d.status === ApprovalStatus.Rejected;
      }

      /** The payload flavor a device updates from. The port of `AgentPayloads.FlavorFor`. */
      function flavorFor(d: DeviceDto): string {
        const caption = d.os?.caption ?? '';
        if (isWindowsCaption(caption)) return AgentPayloads.Windows;
        switch (d.os?.architecture ?? '') {
          case 'Arm64':
            return AgentPayloads.SynologyLinuxArm64;
          case 'Arm':
            return AgentPayloads.SynologyLinuxArm;
          default:
            return AgentPayloads.SynologyLinuxX64;
        }
      }

      /**
       * True when the server serves a strictly newer build for THIS device's own flavor.
       *
       * Per-flavor because a Synology agent measured against the Windows payload would read as
       * permanently out of date, which is why the chip used to be suppressed for it entirely.
       */
      function agentOutOfDate(d: DeviceDto): boolean {
        const latest = agentVersions.get(flavorFor(d));
        if (!latest || !d.agentVersion) return false;
        return compareVersions(latest, d.agentVersion) > 0;
      }

      /** Fetches the served version once per flavor present in `list`. */
      async function resolveAgentVersions(list: readonly DeviceDto[]): Promise<void> {
        const flavors = new Set(list.map(flavorFor));
        for (const flavor of flavors) {
          if (agentVersions.has(flavor)) continue;
          try {
            agentVersions.set(flavor, await crm.getLatestAgentVersion(flavor, ctx.dispose.signal()));
          } catch {
            // No published payload for that flavor. No chip, rather than a wrong one.
            agentVersions.set(flavor, null);
          }
        }
      }

      // ---------------------------------------------------------------
      //  Actions
      // ---------------------------------------------------------------

      async function reject(d: DeviceDto): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: 'Reject device',
          message:
            `Reject ${d.name}?\n\nIt won't be managed, but the record is kept - you'll find it under ` +
            '"View archived & rejected devices", where Restore sends it back for approval.'
        });
        if (!ok || ctx.cancelled()) return;
        try {
          await crm.rejectDevice(d.id);
          if (ctx.cancelled()) return;
          status.ok(`Rejected ${d.name}.`);
          await reload();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      /**
       * Tells every online agent to self-update now.
       *
       * Best-effort by design: offline devices are unaffected and catch up on their next connect, so
       * the message says what was pushed rather than claiming a fleet-wide outcome.
       */
      async function pushUpdate(): Promise<void> {
        if (pushing) return;
        pushing = true;
        pushStatus.busy('Pushing…');
        try {
          const version = await crm.pushAgentUpdate();
          if (ctx.cancelled()) return;
          pushStatus.set(
            version === null
              ? 'Push failed — is the agent payload published?'
              : `Update ${version} pushed to online agents.`,
            version === null ? 'error' : 'ok'
          );
        } catch (error) {
          if (!ctx.cancelled()) pushStatus.error(describeError(error));
        } finally {
          pushing = false;
        }
      }

      // ---------------------------------------------------------------
      //  Loading
      // ---------------------------------------------------------------

      async function switchView(next: View): Promise<void> {
        view = next;
        status.clear();
        // Cleared rather than diffed across the switch: the two row shapes are different builders,
        // and a kept control would be the wrong shape for the list it survived into.
        deviceList.clear();
        applyChrome();
        await reload();
      }

      function selectedCustomerId(): number | undefined {
        const index = customerFilter.selectedIndex;
        return index > 0 && index - 1 < choices.ids.length ? choices.ids[index - 1] : undefined;
      }

      async function customerChanged(): Promise<void> {
        status.clear();
        deviceList.clear();
        applyChrome();
        await reload();
      }

      function applyChrome(): void {
        subtitle.text =
          view === 'pending'
            ? "Enrolled but not yet approved — they aren't managed until you approve them."
            : view === 'retired'
              ? 'Archived and rejected devices — open one to restore or permanently delete it.'
              : 'Live status updates automatically.';
        backLink.visible = view !== 'active';
        retiredLink.visible = view === 'active';
        const filtered = selectedCustomerId() !== undefined;
        devicesEmpty.message =
          view === 'pending'
            ? filtered ? 'Nothing from this customer is waiting for approval.' : 'Nothing is waiting for approval.'
            : view === 'retired'
              ? filtered ? 'This customer has no archived or rejected devices.' : 'No archived or rejected devices.'
              : filtered ? 'This customer has no devices.' : 'No devices yet.';
        relayout(content);
      }

      function applyPendingLink(count: number): void {
        // Only worth offering the queue when something is in it.
        pendingLink.visible = view === 'active' && count > 0;
        pendingLink.text =
          count === 1
            ? '1 device is waiting for approval →'
            : `${count} devices are waiting for approval →`;
      }

      async function reload(): Promise<void> {
        const mine = ++generation;
        const stale = () => ctx.cancelled() || mine !== generation;
        const customerId = selectedCustomerId();
        try {
          // Always counted, whichever view is showing: it drives the prompt on the managed list.
          const pending = await crm.getPendingDevices(customerId, ctx.dispose.signal());
          if (stale()) return;
          applyPendingLink(pending.length);

          if (view === 'pending') {
            await resolveAgentVersions(pending);
            if (stale()) return;
            devices = pending;
            deviceList.set(pending);
            devicesEmpty.update(pending.length);
            monitorList.clear();
            monitorSection.visible = false;
            relayout(content);
            return;
          }

          const list =
            view === 'retired'
              ? await crm.getArchivedDevices(customerId, ctx.dispose.signal())
              : await crm.getDevices(customerId, ctx.dispose.signal());
          if (stale()) return;
          await resolveAgentVersions(list);
          if (stale()) return;
          devices = list;
          deviceList.set(list);
          devicesEmpty.update(list.length);

          await refreshMonitors();
          relayout(content);
        } catch (error) {
          if (!stale()) status.error(describeError(error));
        }
      }

      /**
       * The monitored list appears only under the active fleet.
       *
       * The retired and pending views are about agent lifecycle, which an agentless device does not
       * have - so a monitor has no state that either view could show.
       */
      async function refreshMonitors(): Promise<void> {
        if (view !== 'active') {
          monitorList.clear();
          monitorSection.visible = false;
          return;
        }
        const customerId = selectedCustomerId();
        try {
          const monitors = await crm.getAllMonitors(ctx.dispose.signal());
          if (ctx.cancelled() || customerId !== selectedCustomerId()) return;
          const agentless = monitors.filter(m => !m.hasAgent && (customerId === undefined || m.customerId === customerId));
          monitorList.set(
            agentless.map(m => ({ monitor: m, customerName: customerNames.get(m.customerId) ?? '' }))
          );
          monitorSection.visible = agentless.length > 0;
        } catch {
          // A failed refresh keeps the rows it has, like the presence poll: a momentary outage is
          // not evidence that the monitors went away.
        }
      }

      /**
       * Applies presence to the rows on screen.
       *
       * A device with no entry is reported offline rather than left as it was: absent from the
       * presence read means the tracker has no connection for it, which IS the offline answer.
       */
      function applyPresence(list: readonly DevicePresenceDto[]): void {
        const byId = new Map(list.map(p => [p.deviceId, p]));
        devices = devices.map(d => ({
          ...d,
          presence:
            byId.get(d.id) ??
            ({ deviceId: d.id, isOnline: false, loggedOnUser: null, sessionState: 0, lastSeenUtc: null } as DevicePresenceDto)
        }));
        deviceList.set(devices);
      }

      async function pollPresence(): Promise<void> {
        try {
          const presences = await crm.getAllPresence(ctx.dispose.signal());
          if (ctx.cancelled()) return;
          applyPresence(presences);
          if (++tick >= MONITOR_EVERY_TICKS) {
            tick = 0;
            await refreshMonitors();
          }
          relayout(content);
        } catch {
          // A failed poll leaves the last known state alone - see `applyPresence`'s own note.
        }
      }

      choices = await loadCustomerChoices(ctx.dispose.signal());
      if (ctx.cancelled()) return;
      customerNames = new Map(choices.ids.map((id, i) => [id, choices.names[i] ?? '']));
      customerFilter.items = ['All customers', ...choices.names];
      customerFilter.selectedIndex = 0;
      ready = true;

      applyChrome();
      await reload();
      if (ctx.cancelled()) return;

      ctx.dispose.interval(() => void pollPresence(), PRESENCE_POLL_MS);

      // Live presence on top of the poll. Failure is not fatal: the page is correct as of its last
      // read either way, and the poll is what corrects it if a push never arrives.
      if (await hub.ensureConnected()) {
        if (ctx.cancelled()) return;
        ctx.dispose.add(
          hub.on('DevicePresenceChanged', presence => {
            const at = devices.findIndex(d => d.id === presence.deviceId);
            if (at < 0) return;
            devices = devices.map((d, i) => (i === at ? { ...d, presence } : d));
            deviceList.set(devices);
            relayout(content);
          })
        );
      }
    }
  };
}

/**
 * Compares two dotted version strings.
 *
 * Numeric per component, which `localeCompare` is not: "1.10.0" is newer than "1.9.0" and a string
 * comparison says the opposite - so an agent one minor version behind would read as up to date.
 */
export function compareVersions(a: string, b: string): number {
  const left = a.split('.').map(n => Number.parseInt(n, 10) || 0);
  const right = b.split('.').map(n => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
